// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  GuestWaitingList,
  LookEntity,
  ParkingTicketsStatus,
  SeatDistributionMode,
  SeatStatus,
  SurveyExtensionPoint,
  WaitingListStatusEnum,
  guestKeys,
} from '@seaters/constants';
import {
  TicketsUrl,
  downloadFileByURL,
  fetchGuestWaitingList,
  getMapTranslation,
  getTicketsFileName,
  useAcceptGuestWaitingList,
  useCheckoutStore,
  useExportGuestWaitingListParkingSeats,
  useExportGuestWaitingListSeats,
  useFetchGuestSurvey,
  useGuestCancelWaitingList,
  useUpdateGuestWaitingListSeats,
} from '@seaters/data-access';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import {
  Card,
  ConfigProvider,
  Flex,
  Form,
  notification,
  Popconfirm,
  Result,
  Select,
  theme,
} from 'antd';
import dayjs from 'dayjs';

import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { RejectSeatsButton } from './RejectSeatsButton';
import { useTranslation } from 'react-i18next';
import { CheckoutForm } from './CheckoutForm';
import { useEffect, useState } from 'react';
import { Button, LexicalEditor } from '@seaters/ui';
import AdjustAttendeeList from './AdjustAttendeeList';
import { CardHeading } from './CardHeading';

dayjs.extend(LocalizedFormat);

export type RefetchingStatus = 'join' | 'cancel' | null;

type TicketsSelectCardProps = {
  fanGroupData: LookEntity;
};

const getTicketsConfirmed = (waitingListData?: GuestWaitingList) => {
  return waitingListData?.seat.status === SeatStatus.ACCEPTED;
};

const getTicketsConfirmedWithoutSeat = (waitingListData?: GuestWaitingList) => {
  return waitingListData?.seat.status === SeatStatus.RSVP_ACCEPTED;
};

const checkSeatConfirmation = (wishList: GuestWaitingList) => {
  if (
    wishList &&
    wishList.seat &&
    wishList.seat.status === SeatStatus.ACCEPTED
  ) {
    if (wishList.seatDistributionMode === SeatDistributionMode.VOUCHER) {
      return true;
    } else if (wishList.seatDistributionMode === SeatDistributionMode.TICKET) {
      // TicketingSystemType is populated when done
      return typeof wishList.seat.ticketingSystemType === 'string';
    }
  }
  // All other cases are still in progress
  return false;
};
const checkWithoutSeatConfirmation = (wishList: GuestWaitingList) => {
  if (
    wishList &&
    wishList.seat &&
    wishList.seat.status === SeatStatus.RSVP_ACCEPTED
  ) {
    return true;
  }
  // All other cases are still in progress
  return false;
};

export function TicketsSelectCard({ fanGroupData }: TicketsSelectCardProps) {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { language } = i18n;
  const lang = language === 'nl_NL' || language === 'nl_BE' ? 'nl' : language;
  const [loading, setLoading] = useState<TicketsUrl>();
  const [wlRefetching, setWlRefetching] = useState(false);
  const [vouchersUrl, setVouchersUrl] = useState<string | null>(null);

  const { setCheckoutShown } = useCheckoutStore();

  const [refetchingStatus, setRefetchingStatus] =
    useState<RefetchingStatus>(null);

  const searchParams = new URLSearchParams(window.location.search);

  const waitingListId = searchParams.get('waitingListId') || '';
  const code = searchParams.get('code') || '';
  const isHost = searchParams.get('host') === 'true';

  const allMutations = !!useIsMutating();

  const { data: surveyInstances } = useFetchGuestSurvey({
    code,
    waitinglist_id: waitingListId,
    extension_point: SurveyExtensionPoint.AT_CHECKOUT,
  });

  const { data: waitingListData } = useQuery<GuestWaitingList, Error>({
    queryKey: guestKeys.waitingList(waitingListId),
    queryFn: () => fetchGuestWaitingList(waitingListId, { code }),
    enabled: !!waitingListId,
    refetchInterval({ state: { data } }) {
      const hasSeat =
        data &&
        (checkSeatConfirmation(data) || checkWithoutSeatConfirmation(data));
      const hasNewUrl = data?.seat.exportedVoucherUrl !== vouchersUrl;
      if (hasSeat && !wlRefetching && loading !== 'exportedVoucherUrl') {
        return false;
      }
      if (
        hasSeat &&
        wlRefetching &&
        (loading !== 'exportedVoucherUrl' || hasNewUrl)
      ) {
        handleRefetchingEnd();
      }
      if (wlRefetching) {
        return 1000;
      }
      return false;
    },
  });

  const eventDisplayName =
    waitingListData?.translatedEventName ??
    (getMapTranslation(waitingListData?.experienceName, lang) ||
      getMapTranslation(waitingListData?.eventName, lang));
  // Experience first, matching the fan page title: experiences built on a shared
  // event would otherwise all download under the same name.
  const ticketsFileName = getTicketsFileName(
    getMapTranslation(waitingListData?.experienceName, lang) ||
      getMapTranslation(waitingListData?.eventName, lang) ||
      waitingListData?.translatedEventName,
    t('group_numberofseats')
  );

  const hasSeat = waitingListData?.position?.status === 'HAS_SEAT';
  const hasPosition = waitingListData?.position;

  const { mutate: guestCancelWaitingList } = useGuestCancelWaitingList(
    waitingListId,
    { code }
  );

  const rejectWaitingList = () => {
    guestCancelWaitingList(
      { waitingListId },
      {
        onSuccess: () => {
          setRefetchingStatus('cancel');
          notification.success({
            message: t('booking_cancellation_notification_success'),
          });
        },
        onError: (err) => {
          console.error(err);
          notification.error({
            message: t('booking_cancellation_notification_error'),
          });
        },
      }
    );
  };

  const { mutate: acceptAsGuest } = useAcceptGuestWaitingList(waitingListId, {
    code,
  });

  const { mutate: updateNbrOfSeats } = useUpdateGuestWaitingListSeats(
    waitingListId,
    {
      code,
    }
  );

  const { mutateAsync: exportSeats, isPending: isLoadingSeats } =
    useExportGuestWaitingListSeats(waitingListId, {
      code,
    });

  const { mutateAsync: exportParkingSeats, isPending: isLoadingParkingSeats } =
    useExportGuestWaitingListParkingSeats(waitingListId, {
      code,
    });

  const parkingTicketsConfirmed =
    waitingListData?.seat.parkingTicketStatus === ParkingTicketsStatus.ACCEPTED;

  const ticketsConfirmed = getTicketsConfirmed(waitingListData);
  const ticketsConfirmedWithoutSeat =
    getTicketsConfirmedWithoutSeat(waitingListData);
  const waitForTicket =
    waitingListData?.seat.status === SeatStatus.RSVP_ACCEPTED;

  const getSelectOptions = () => {
    const arr = [];

    for (let i = 0; i < (waitingListData?.position.numberOfSeats ?? 0); i++) {
      arr.push({
        label: i + 1,
        value: i + 1,
      });
    }

    return arr;
  };

  let refetchCount = 0;

  const handleRefetchingStart = () => {
    setWlRefetching(true);
  };

  const handleRefetchingEnd = () => {
    setWlRefetching(false);

    notification.success({
      message:
        loading === 'exportedVoucherUrl'
          ? t('guest_app_vouchers_reexported_notification')
          : t('guest_app_accepted_notification'),
    });

    setLoading(undefined);
  };

  const handleAcceptAsGuest = () => {
    acceptAsGuest(undefined, {
      onSuccess: () => {
        handleRefetchingStart();
      },
    });
  };

  const handleTicketsSelect = (value: number) => {
    if (waitingListData?.position.numberOfSeats !== value) {
      updateNbrOfSeats(
        { nbrOfSeats: value },
        { onSuccess: () => console.log('updated') }
      );
    }
  };

  const handleAcceptTickets = () => {
    if (
      waitingListData?.eventRequiredAttendeeInfo?.length ||
      !!surveyInstances?.content.length
    ) {
      setCheckoutShown(true);
    } else if (isHost) {
      console.log('host');
    } else {
      handleAcceptAsGuest();
    }
  };

  const downloadRepeater = async (ticketsUrl: TicketsUrl) => {
    const waitingList = await fetchGuestWaitingList(waitingListId, { code });

    const ticketsReady = waitingList.seat[ticketsUrl];

    const resetCounter = () => (refetchCount = 0);

    if (refetchCount <= 5) {
      if (ticketsReady) {
        resetCounter();
        try {
          await downloadFileByURL(
            waitingList.seat[ticketsUrl] || '',
            ticketsUrl === 'exportedParkingTicketUrl'
              ? 'Parking tickets'
              : ticketsFileName
          );
        } catch (error) {
          notification.error({
            message: t('tickets_generate_notification_error'),
          });
        } finally {
          setLoading(undefined);
        }
      } else {
        refetchCount += 1;
        setTimeout(() => downloadRepeater(ticketsUrl), 1000);
      }
    }
  };

  const handleTicketsDownload = async () => {
    setLoading('exportedVoucherUrl');

    const exportedVoucherUrl = waitingListData?.seat.exportedVoucherUrl;
    if (exportedVoucherUrl) {
      try {
        await downloadFileByURL(exportedVoucherUrl, ticketsFileName);
      } catch (error) {
        notification.error({
          message: t('tickets_generate_notification_error'),
        });
      } finally {
        setLoading(undefined);
      }
    } else {
      await exportSeats(undefined, {
        onSuccess: async () => {
          downloadRepeater('exportedVoucherUrl');
        },
        onError: () => {
          notification.error({
            message: t('tickets_generate_notification_error'),
          });
          setLoading(undefined);
        },
      });
    }
  };

  const handleParkingTicketDownload = async () => {
    setLoading('exportedParkingTicketUrl');
    const exportedParkingTicketUrl =
      waitingListData?.seat.exportedParkingTicketUrl;
    if (exportedParkingTicketUrl) {
      try {
        await downloadFileByURL(exportedParkingTicketUrl, 'Parking tickets');
      } catch (error) {
        notification.error({
          message: t('tickets_generate_notification_error'),
        });
      } finally {
        setLoading(undefined);
      }
    } else {
      await exportParkingSeats(undefined, {
        onSuccess: async () => {
          downloadRepeater('exportedParkingTicketUrl');
        },
        onError: () => {
          notification.error({
            message: t('tickets_generate_notification_error'),
          });
          setLoading(undefined);
        },
      });
    }
  };

  const seatsConfirmed = ticketsConfirmed || ticketsConfirmedWithoutSeat;

  const isAbleToAdjustAttendees =
    waitingListData?.eventRequiredAttendeeInfo &&
    waitingListData?.seat.status === SeatStatus.ACCEPTED &&
    waitingListData?.seatDistributionMode === SeatDistributionMode.VOUCHER &&
    waitingListData?.freeWaitingList;

  const isAbleToLeaveWL =
    waitingListData?.waitingListStatus === WaitingListStatusEnum.OPEN &&
    waitingListData?.seatDistributionMode === SeatDistributionMode.VOUCHER &&
    waitingListData?.freeWaitingList &&
    waitingListData?.seat?.status === SeatStatus.ACCEPTED;

  useEffect(() => {
    setVouchersUrl(waitingListData?.seat?.exportedVoucherUrl ?? null);
  }, [waitingListData]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerFontSize: 14,
            /* here is your component tokens */
          },
        },
      }}
    >
      <Card
        title={
          !seatsConfirmed && (
            <CardHeading>
              {t('guest_app_questionForHowManyTickets')}
            </CardHeading>
          )
        }
      >
        <Flex vertical justify="center" align="center">
          {ticketsConfirmed ? (
            <Result
              status="success"
              title={t('guest_app_thanks')}
              extra={[
                <Button
                  type="primary"
                  key="console"
                  size="large"
                  data-testid="getTicketsButton"
                  onClick={handleTicketsDownload}
                  loading={loading === 'exportedVoucherUrl'}
                >
                  {t('guest_app_getTickets')}
                </Button>,
                parkingTicketsConfirmed && (
                  <Button
                    size="large"
                    type="primary"
                    disabled={allMutations}
                    onClick={handleParkingTicketDownload}
                  >
                    {t('guest_app_getParkingTickets')}
                  </Button>
                ),
                isAbleToAdjustAttendees && (
                  <AdjustAttendeeList
                    waitingList={waitingListData}
                    setWlRefetching={setWlRefetching}
                    setLoading={setLoading}
                  />
                ),
              ]}
            />
          ) : ticketsConfirmedWithoutSeat ? (
            <Result
              status="success"
              title={t('guest_app_thanks_wo_seats-title')}
              subTitle={t('guest_app_thanks_wo_seats-subtitle')}
            />
          ) : (
            <Form.Item label={t('guest_app_quantity')} htmlFor="ticketQuantity">
              <Select
                id="ticketQuantity"
                data-testid="ticketQuantitySelect"
                disabled={allMutations || wlRefetching}
                options={getSelectOptions()}
                style={{ width: 120 }}
                onChange={handleTicketsSelect}
                value={waitingListData?.position.numberOfSeats}
              />
            </Form.Item>
          )}
          {isAbleToLeaveWL && (
            <Popconfirm
              title={t('wishlist_leave_wishlist_modal_title')}
              onConfirm={rejectWaitingList}
              description={
                <LexicalEditor
                  borderless
                  compact
                  disabled
                  placeholder=""
                  toolbar={false}
                  value={String(
                    t('wishlist_leave_wishlist_modal_message', {
                      wishlistname: eventDisplayName,
                    })
                  )}
                />
              }
              cancelText={t('general_no')}
            >
              <Button type="link" color={token.colorError}>
                {t('leave_wl_with_tickets_button_text')}
              </Button>
            </Popconfirm>
          )}
          <Flex justify="space-around" style={{ width: '100%' }}>
            {!seatsConfirmed && !waitForTicket && (
              <RejectSeatsButton disabled={allMutations || wlRefetching} />
            )}

            {/* regular tickets */}
            {!seatsConfirmed && (
              <Button
                size="large"
                type="primary"
                disabled={allMutations}
                onClick={handleAcceptTickets}
                loading={wlRefetching}
                data-testid="confirmSeatsButton"
              >
                {t('guest_app_confirm')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>
      {hasSeat && hasPosition && (
        <CheckoutForm
          waitingListData={waitingListData}
          handleAcceptAsGuest={handleAcceptAsGuest}
          isLoading={wlRefetching}
        />
      )}
    </ConfigProvider>
  );
}
